import { useEffect } from "react";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import Routes from "./routes";
import { ToastContainer } from "react-toastify";
import { Provider, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { store } from "./redux/store/store";
import { isDarkModeAllowed } from "./config/routePermissions";
import { setupVisibilityRefresh, setupWorkerRefresh, stopWorkerRefresh } from "./utils/tokenRefresh";

// route renders light so un-tokenized pages never clash with the dark chrome.
function ThemeSync() {
  const mode = useSelector((state) => state.theme.mode);
  const role = useSelector((state) => state.auth.role);
  const { pathname } = useLocation();
  useEffect(() => {
    const effective =
      mode === "dark" && isDarkModeAllowed(role, pathname) ? "dark" : "light";
    document.documentElement.dataset.theme = effective;
  }, [mode, role, pathname]);
  return null;
}


function App() {
  useEffect(() => {
    // Setup visibility change listener (instant check when user returns to tab)
    const cleanupVisibility = setupVisibilityRefresh();
    // Web Worker handles background refresh reliably (not throttled by browser)
    const worker = setupWorkerRefresh();
    return () => {
      if (cleanupVisibility) cleanupVisibility();
      if (worker) stopWorkerRefresh();
    };
  }, []);

  useEffect(() => {
    // Download the CCTV AI detection model in the background as soon as the site
    // opens, so the first Detect starts at once. The SDK waits for page load and
    // idle time and caches it once per browser.
    if (!process.env.REACT_APP_VMS_KEY) return;
    import("@codebucket/vms/detect")
      .then(({ preloadDetection }) => preloadDetection())
      .catch(() => {});
  }, []);

  return (
    <div className="App">
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.REACT_APP_RE_CAPTCHA_SITE_KEY}
        scriptProps={{ defer: true, appendTo: "head" }}
      >
        <Provider store={store}>
          <ThemeSync />
          <ToastContainer />
          <Routes />
        </Provider>
      </GoogleReCaptchaProvider>
    </div>
  );
}

export default App;
